import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { color as echartsColor } from 'echarts';
import { useTranslation } from 'react-i18next';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../CustomDashboard/WidgetRenderer/echartsUtils';
import styles from './VerticalBarChart.module.scss';

export interface VerticalBarDatum {
  name: string;
  value: number;
  /** Per-bar color override; defaults to the primary bar color. */
  color?: string;
}

interface VerticalBarChartProps {
  chartData: VerticalBarDatum[] | null;
  isClickable?: boolean;
  /** Receives the index of the clicked bar within `chartData`. */
  onSelect?: (selected: number | null) => void;
  height?: number;
  seriesName?: string;
  /** Bar width as a percentage of the category slot. */
  barWidth?: string;
}

// echarts SVG text cannot read CSS variables, so the palette is mirrored here.
export const BAR_COLOR = '#2463EB';
const AXIS_TEXT_COLOR = '#4f546c'; // --clr-gray500
const VALUE_TEXT_COLOR = '#151419'; // --main-color
const GRID_LINE_COLOR = '#f0f0f0';

export const VerticalBarChart = ({
  chartData,
  isClickable = true,
  onSelect = () => {},
  height = 400,
  seriesName,
  barWidth = '55%',
}: VerticalBarChartProps) => {
  const { t } = useTranslation();
  const items = useMemo(() => chartData ?? [], [chartData]);

  const option = useMemo<EChartsOption>(
    () => ({
      aria: { enabled: true },
      grid: { left: 16, right: 16, top: 32, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        ...ECHARTS_TOOLTIP_STYLE,
        formatter: (params: any) => {
          const item = items[params.dataIndex];
          if (!item) return '';
          return `${params.marker}${escapeHtml(item.name)}: <b>${escapeHtml(
            formatWithCustomGigaBillion(item.value),
          )}</b>`;
        },
      },
      xAxis: {
        type: 'category',
        data: items.map((item) => item.name),
        axisLabel: { fontSize: 14, color: AXIS_TEXT_COLOR, interval: 0, hideOverlap: true },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: { fontSize: 14, color: AXIS_TEXT_COLOR, formatter: (val: number) => formatWithCustomGigaBillion(val) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
      },
      series: [
        {
          type: 'bar',
          name: seriesName,
          barWidth,
          cursor: isClickable ? 'pointer' : 'default',
          data: items.map((item) => {
            const color = item.color ?? BAR_COLOR;
            return {
              value: item.value,
              itemStyle: { color },
              // Google-charts style hover: a thin outline in a darker shade of
              // the bar's own color. The fill is repeated explicitly, otherwise
              // echarts "lifts" it to a lighter tint on hover.
              emphasis: {
                itemStyle: { color, borderColor: echartsColor.lerp(0.35, [color, '#000']), borderWidth: 2 },
              },
            };
          }),
          label: {
            show: true,
            position: 'top',
            distance: 8,
            fontSize: 12,
            fontWeight: 'bold',
            color: VALUE_TEXT_COLOR,
            formatter: (params: any) => String(params.value),
          },
          // No `emphasis.focus` blur: dimming the other bars on every hover
          // flickers as the pointer crosses bar gaps. Only the hovered bar
          // changes (per-item emphasis above).
        },
      ],
    }),
    [items, isClickable, seriesName, barWidth],
  );

  const onEvents = useMemo(() => {
    if (!isClickable) return undefined;
    return {
      click: (params: any) => {
        const index = typeof params?.dataIndex === 'number' ? params.dataIndex : null;
        onSelect(index);
      },
    };
  }, [isClickable, onSelect]);

  if (chartData === null) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <LoaderSmall />
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>{t('There is no data to display')}</div>
      </div>
    );
  }

  return (
    <div className={styles.container} translate="no">
      <ReactECharts
        option={option}
        notMerge
        style={{ width: '100%', height: `${height}px` }}
        opts={{ renderer: 'svg' }}
        onEvents={onEvents}
      />
    </div>
  );
};
